import { type FC } from 'react';
import {
  Flex,
  IconButton,
  Stack,
  useDisclosure
} from '@chakra-ui/react';
import {
  UiText,
  UiIconList,
  UiHStack,
  UiStack,
  UiIconHouse,
  UiDrawer,
  UiDrawerOverlay,
  UiDrawerContent,
  UiDrawerCloseButton,
  UiDrawerBody,
  UiHStackProps, uiStyles,
} from '@/lib/ui';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { ElementType, useMemo } from 'react';
import { useTenantRoute } from '@/account/hook/useTenantRoute';
import { generatePageUrl } from '@/app/pages';
import BaseBlockList from '@/base/BlockList';
import BaseRouterLink from '@/base/Router/Link';
import { topNavHeight } from '@/base/Layout/viewport';

function Header() {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <>
      <UiStack
        top={0}
        left={0}
        flexGrow={1}
        bgColor={'transparent'}
      >
        <Flex
          color={'blackAlpha.600'}
          minH={`${topNavHeight}px`}
          maxH={`${topNavHeight}px`}
          py={{ base: 4 }}
          px={{ base: 4, lg: 8 }}
          align={'center'}
        >
          <Flex
            flex={{ base: 1, md: 'auto' }}
          >
            <IconButton
              onClick={onToggle}
              // icon={
              //   isOpen ? <UiIconX size={'3xl'} /> : <UiIconList size={'3xl'} />
              // }
              icon={(
                <UiIconList size={'3xl'} />
              )}
              variant={'ghost'}
              aria-label={'Toggle Navigation'}
            />
          </Flex>
          <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }} align={'center'}/>
        </Flex>
      </UiStack>
      <MenuDrawer isOpen={isOpen} onClose={onToggle} />
    </>
  );
}

interface MenuDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItem {
  label: string
  description?: string
  url?: string
  Icon?: ElementType
}

const MenuDrawer = ({
  onClose = () => {},
  isOpen = false
}: MenuDrawerProps) => {
  const { eventId } = useRegisterRoute();
  const { tenant } = useTenantRoute();

  const menuList: MenuItem[] = useMemo(
    () => {
      if (tenant?.code && eventId) {
        return [
          {
            label: 'Event home',
            description: 'Beginning of the registration',
            url: generatePageUrl('RegistrationRegisterEmail', { tenantCode: tenant?.code, eventId }),
            Icon: UiIconHouse,
          }
        ];
      }
      return [];
    },
    [
      tenant?.code,
      eventId,
    ]
  );

  return (
    <UiDrawer
      placement={'left'}
      size={'lg'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <UiDrawerOverlay />
      <UiDrawerContent>
        <UiDrawerCloseButton size={'lg'} color={'primary.500'}/>
        <UiDrawerBody px={{base: 4, lg: 8}} bgColor={'gray.100'} py={16}>
          <BaseBlockList bgColor={'#fff'}>
            {menuList.map((menuItem, index) => {
              return (
                <MenuDrawerItem key={index} data={menuItem} {...uiStyles.hover}/>
              );
            })}
          </BaseBlockList>
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

interface MenuDrawerItemProps extends UiHStackProps {
  data: MenuItem
  isActive?: boolean
}

const MenuDrawerItem: FC<MenuDrawerItemProps> = ({
  data,
  isActive = false,
  ...props
}) => {
  // const { isOpen, onToggle } = useDisclosure();
  return (
    <BaseRouterLink to={data.url ?? '#'}>
      <UiHStack spacing={4} {...props} px={{base: 4, lg: 8}} py={4}>
        {!!data.Icon && (
          <data.Icon size={'3xl'}/>
        )}
        <UiStack spacing={0}>
          <UiText variant={'body1'} fontWeight={600}>{data.label}</UiText>
          {!!data.description && (
            <UiText variant={'body2'} color={'text.secondary'}>{data.description}</UiText>
          )}
        </UiStack>
      </UiHStack>
    </BaseRouterLink>
  );
};

export default Header;
